import { useState, memo } from 'react';
import { deleteSession, getPermissions, isBpeTM } from 'utils/userUtils';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuItem from 'components/MenuItem';
import List from '@mui/material/List';
import Button from 'components/Button';
import TextField from 'components/TextField';
import {
  HomeRounded,
  TaskRounded,
  InsertDriveFileRounded,
  BarChartRounded,
  SegmentRounded,
  SettingsRounded,
  TextSnippetRounded,
  PrivacyTipRounded,
  HelpRounded,
  LogoutRounded,
  SupportAgentRounded,
  AssignmentLate,
  LogoDev,
  RequestQuoteRounded,
  SearchRounded,
  StarRounded
} from '@mui/icons-material';
import { getUserSession } from 'utils/userUtils';
import { useFavorites } from 'hooks/useFavorites';
import companyAPI from 'apis/CompanyAPI';

const normalize = (text) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function SideMenu({ collapsed = false }) {
  const { t } = useTranslation(['Pages', 'Actions']);
  const { email } = getUserSession();
  const { favorites } = useFavorites();
  const isBpe = isBpeTM();
  const [search, setSearch] = useState('');

  const [privacyPolicyNotFoundMsg, setPrivacyPolicyNotFoundMsg] = useState(false);
  const userAgreementLink = 'https://materiais.praxio.com.br/termos-de-uso-siga-emissor';
  const termsAndConditionsLink = 'https://www.praxio.com.br/downloaddocumentos/TermosECondicoesGeraisSigaEmissor.pdf';
  const expiredValidity = window.sessionStorage.getItem("expiredValidity");

  const getPrivacyPolicy = () => {
    companyAPI.getPrivacyPolice()
      .then(resp => {
        resp.blob().then(resp => {
          var fileURL = window.URL.createObjectURL(resp);
          let tab = window.open();
          tab.location.href = fileURL;
        });
      })
      .catch((_error) => {
        setPrivacyPolicyNotFoundMsg(true);
      });
  };

  // Empresa BP-e TM não usa Clientes, Motoristas, Transportadores e Alíquotas:
  // essas rotinas saem da visualização, como já acontece nos campos de CTe-OS
  // do cadastro da filial. Linhas é o caminho inverso — LINHAS_BPETM só existe
  // para o BP-e TM (a API responde em /linhaBPeTM, com validTipoEmissao(2)).
  const cadastrosChildren = [
    { title: t('Clientes'), route: '/clientes', hidden: isBpe || !getPermissions('customers').view },
    { title: t('Veículos'), route: '/veiculos', hidden: !getPermissions('vehicles').view },
    { title: t('Motoristas'), route: '/motoristas', hidden: isBpe || !getPermissions('drivers').view },
    { title: t('Transportadores'), route: '/transportadores', hidden: isBpe || !getPermissions('transporters').view },
    { title: t('Alíquotas'), route: '/aliquotas', hidden: isBpe || !getPermissions('taxrates').view },
    { title: t('Linhas'), route: '/linhas', hidden: !isBpe || !getPermissions('lines').view }
  ];

  const menu = [
    {
      title: t('Dashboard'),
      icon: <HomeRounded />,
      route: '/'
    },
    {
      title: t('Cadastros'),
      icon: <TaskRounded />,
      hidden: cadastrosChildren.every(child => child.hidden),
      children: cadastrosChildren
    },
    //   title: t('Tabelas'),
    //   icon: <TableRowsRounded/>,
    //   hidden:
    //     !getPermissions('routes').view &&
    //     !getPermissions('padraoVeic').view &&
    //     !getPermissions('accessVehic').view &&
    //     !getPermissions('ranges').view,
    //   children: [
    //     { title: t('Acessórios e Extras'), route: '/acessorios_extras', hidden: !getPermissions('accessVehic').view },
    //     { title: t('Vendedores'), route: '/vendedores', hidden: !getPermissions('sellers').view },
    //     { title: t('Rotas e Trajetos'), route: '/rotas', hidden: !getPermissions('routes').view },
    //     { title: t('Padrões de Veículo'), route: '/padroes_veiculo', hidden: !getPermissions('padraoVeic').view },
    //     { title: t('Faixas'), route: '/faixas', hidden: !getPermissions('ranges').view }
    //   ]
    // },
    {
      title: t('Lançamentos'),
      icon: <InsertDriveFileRounded />,
      hidden:
        //!getPermissions('EventualContracts').view &&
        //!getPermissions('budgets').view &&
        (isBpe || !getPermissions('cte-os').view) &&
        (!isBpe || !getPermissions('bpe-tm').view),
      children: [
        //{ title: t('Cotações'), route: '/cotacoes', hidden: !getPermissions('budgets').view },
        //{ title: t('Contrato Eventual'), route: '/contrato_eventual', hidden: !getPermissions('EventualContracts').view },
        // Empresa BP-e TM não emite CTe-OS: o lançamento sai da visualização.
        { title: t('CT-e OS'), route: '/cte-os', hidden: isBpe || !getPermissions('cte-os').view },
        // Empresa CTeOS não emite BP-e TM: o lançamento sai da visualização.
        { title: t('BP-e TM'), route: '/bpe-tm', hidden: !isBpe || !getPermissions('bpe-tm').view }
      ]
    },
    {
      title: t('Relatórios'),
      icon: <BarChartRounded />,
      // Cada produto vê só os seus relatórios; o grupo aparece se houver ao menos um.
      hidden: isBpe
        ? !getPermissions('moviments').view
        : (!getPermissions('moviments').view &&
          !getPermissions('dues').view &&
          !getPermissions('export_documents').view),
      children: [
        // Relatórios de CT-e OS: fora da visualização da empresa BP-e TM.
        { title: t('Movimentos do Mês'), route: '/movimentos', hidden: isBpe || !getPermissions('moviments').view },
        { title: t('Vencimentos'), route: '/vencimentos', hidden: isBpe || !getPermissions('dues').view },
        // Relatório de BP-e TM: só para a empresa BP-e TM. A permissão é a mesma
        // ('moviments'), então os filhos se distinguem por isBpe.
        { title: t('Movimento de BP-e TM'), route: '/movimento-bpetm', hidden: !isBpe || !getPermissions('moviments').view },
        { title: t('Exportar Documentos'), route: '/exportar-documentos', hidden: !getPermissions('export_documents').view }
      ]
    },
    {
      title: t('Organizador'),
      icon: <SegmentRounded />,
      hidden:
        !getPermissions('tasks').view,
      children: [
        { title: t('Tarefas'), route: '/tarefas', hidden: !getPermissions('tasks').view }
      ]
    },
    {
      title: t('Configurações'),
      icon: <SettingsRounded />,
      hidden:
        !getPermissions('user').view &&
        (isBpe || !getPermissions('user_profiles').view) &&
        !getPermissions('companies').view &&
        !getPermissions('establishments').view,
      // !getPermissions('plans').view,
      children: [
        { title: t('Usuários'), route: '/usuarios', hidden: !getPermissions('user').view },
        { title: t('Perfis de Acesso'), route: '/perfis', hidden: isBpe || !getPermissions('user_profiles').view },
        { title: t('Operações'), route: '/operacoes', hidden: isBpe || !getPermissions('operations').view },
        { title: t('Empresa'), route: '/empresa', hidden: !getPermissions('companies').view },
        { title: t('Filial'), route: '/filial', hidden: !getPermissions('establishments').view }
        // { title: t('Planos'), route: '/planos', hidden: !getPermissions('plans').view }
      ]
    },
    {
      title: t('Log IA'), hidden: !email.includes('@praxio.com.br'),
      icon: <LogoDev />,
      route: 'log-ia'
    },
    {
      title: t('2ª Via de Boleto'),
      icon: <RequestQuoteRounded />,
      route: '/segunda-via-boleto',
      allowLogoutUse: true
    },
    {
      title: t('Política de Privacidade'),
      icon: <TextSnippetRounded />,
      onClick: () => getPrivacyPolicy()
    },
    {
      title: t('Termos de Uso'),
      icon: <PrivacyTipRounded />,
      onClick: () => window.open(userAgreementLink, '_blank')
    },
    {
      title: t('Termos Gerais'),
      icon: <AssignmentLate />,
      onClick: () => window.open(termsAndConditionsLink, '_blank')
    },
    {
      title: t('Suporte ao Cliente'),
      icon: <SupportAgentRounded />,
      route: 'suporte',
      allowLogoutUse: true
    },
    {
      title: t('Ajuda'),
      icon: <HelpRounded />,
      route: 'ajuda',
    },
    {
      title: t('Sair', { ns: 'Actions' }),
      icon: <LogoutRounded />,
      onClick: () => deleteSession(),
      allowLogoutUse: true
    }
  ];

  // Resolve visibilidade (permissões + validade expirada) antes de busca/favoritos.
  const visibleMenu = menu.map((item) => ({
    ...item,
    hidden: item.hidden || (expiredValidity && !item.allowLogoutUse)
  }));

  // Entrada "Favoritos" — agrupa subitens favoritados logo após o Dashboard.
  const allChildren = visibleMenu
    .filter((item) => !item.hidden)
    .flatMap((item) => item.children ?? []);
  const favoriteChildren = favorites
    .map((route) => allChildren.find((c) => c.route === route && !c.hidden))
    .filter(Boolean);

  const menuWithFavorites = favoriteChildren.length
    ? [
      visibleMenu[0],
      {
        title: 'Favoritos',
        icon: <StarRounded />,
        children: favoriteChildren
      },
      ...visibleMenu.slice(1)
    ]
    : visibleMenu;

  const term = normalize(search.trim());
  const filteredMenu = term
    ? menuWithFavorites
      .map((item) => {
        if (normalize(item.title).includes(term)) return item;
        const children = item.children?.filter(
          (child) => !child.hidden && normalize(child.title).includes(term),
        );
        if (children?.length) return { ...item, children };
        return null;
      })
      .filter((item) => item !== null)
    : menuWithFavorites;

  return (
    <div>
      {!collapsed && (
        <TextField
          placeholder='Buscar no menu...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: 1,
            '& .MuiInputBase-root': { fontSize: 12, height: 30 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRounded sx={{ fontSize: '16px ' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      <List disablePadding>
        {filteredMenu.map((item, index) => (
          <MenuItem
            key={item.title ?? index}
            collapsed={collapsed}
            forceOpen={term !== ''}
            {...item}
          />
        ))}
      </List>
      <Dialog
        open={privacyPolicyNotFoundMsg}
        onClose={() => setPrivacyPolicyNotFoundMsg(false)}
      >
        <DialogTitle>
          {'Política de privacidade não encontrada.'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            A empresa ainda não cadastrou uma política de privacidade.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyPolicyNotFoundMsg(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
} export default memo(SideMenu);
